import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  LayoutDashboard, 
  Package, 
  ShoppingBag, 
  DollarSign, 
  Clock, 
  CheckCircle,
  XCircle,
  Truck,
  Plus,
  Edit,
  Trash2,
  Loader2,
  Save,
  X,
  Upload,
  Image as ImageIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Image Upload Component
const ImageUploader = ({ value, onChange, label }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipo de archivo no válido. Solo JPEG, PNG, WebP y GIF.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('El archivo es demasiado grande. Máximo 5MB.');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Construct full URL
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      onChange(fullUrl);
      toast.success('Imagen subida correctamente');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Error al subir la imagen');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <Label className="text-[#A3A3A3]">{label}</Label>
      <div className="flex gap-2">
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="bg-[#0a0a0a] border-[#333] text-white flex-1"
          placeholder="https://... o sube una imagen"
        />
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="hidden"
        />
        <Button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="btn-secondary"
          disabled={uploading}
        >
          {uploading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Upload className="w-4 h-4" />
          )}
        </Button>
      </div>
      {value && (
        <div className="mt-2">
          <img 
            src={value} 
            alt="Preview" 
            className="w-20 h-20 object-cover border border-[#333] rounded"
            onError={(e) => e.target.style.display = 'none'}
          />
        </div>
      )}
    </div>
  );
};

// Color Image Upload Component
const ColorImageUploader = ({ value, onChange, onAdd, colorName }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipo de archivo no válido. Solo JPEG, PNG, WebP y GIF.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('El archivo es demasiado grande. Máximo 5MB.');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      onChange(fullUrl);
      toast.success('Imagen subida correctamente');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Error al subir la imagen');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex gap-2 items-end">
      <div className="flex-1">
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="bg-[#0a0a0a] border-[#333] text-white"
          placeholder="URL de la imagen o sube una"
        />
      </div>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
      />
      <Button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        className="btn-secondary"
        disabled={uploading}
        title="Subir imagen"
      >
        {uploading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Upload className="w-4 h-4" />
        )}
      </Button>
      <Button
        type="button"
        onClick={onAdd}
        className="btn-primary"
        disabled={!colorName || !value}
        title="Añadir color"
      >
        <Plus className="w-4 h-4" />
      </Button>
      {value && (
        <img 
          src={value} 
          alt="Preview" 
          className="w-10 h-10 object-cover border border-[#333] rounded"
          onError={(e) => e.target.style.display = 'none'}
        />
      )}
    </div>
  );
};

// Product Form Component
const ProductForm = ({ product, onSave, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    name_es: product?.name?.es || '',
    name_en: product?.name?.en || '',
    name_fr: product?.name?.fr || '',
    description_es: product?.description?.es || '',
    description_en: product?.description?.en || '',
    description_fr: product?.description?.fr || '',
    price: product?.price || '',
    category: product?.category || 'taser',
    image_url: product?.image_url || '',
    colors: product?.colors?.join(', ') || '',
    in_stock: product?.in_stock ?? true,
    featured: product?.featured ?? false,
    badge: product?.badge || '',
  });

  const [colorImages, setColorImages] = useState(product?.color_images || {});
  const [newColorName, setNewColorName] = useState('');
  const [newColorImage, setNewColorImage] = useState('');

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddColorImage = () => {
    if (newColorName && newColorImage) {
      setColorImages(prev => ({ ...prev, [newColorName]: newColorImage }));
      setNewColorName('');
      setNewColorImage('');
    }
  };

  const handleRemoveColorImage = (colorName) => {
    setColorImages(prev => {
      const updated = { ...prev };
      delete updated[colorName];
      return updated;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const colorsArray = formData.colors
      .split(',')
      .map(c => c.trim())
      .filter(c => c.length > 0);

    const productData = {
      name: {
        es: formData.name_es,
        en: formData.name_en || formData.name_es,
        fr: formData.name_fr || formData.name_es,
      },
      description: {
        es: formData.description_es,
        en: formData.description_en || formData.description_es,
        fr: formData.description_fr || formData.description_es,
      },
      price: parseFloat(formData.price),
      category: formData.category,
      image_url: formData.image_url,
      colors: colorsArray,
      color_images: colorImages,
      in_stock: formData.in_stock,
      featured: formData.featured,
      badge: formData.badge || null,
    };

    onSave(productData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
      {/* Names */}
      <div className="space-y-4">
        <h3 className="text-white font-bold border-b border-[#333] pb-2">Nombre del Producto</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-[#A3A3A3]">Español *</Label>
            <Input
              value={formData.name_es}
              onChange={(e) => handleChange('name_es', e.target.value)}
              className="bg-[#0a0a0a] border-[#333] text-white"
              required
            />
          </div>
          <div>
            <Label className="text-[#A3A3A3]">English</Label>
            <Input
              value={formData.name_en}
              onChange={(e) => handleChange('name_en', e.target.value)}
              className="bg-[#0a0a0a] border-[#333] text-white"
              placeholder="(usa español si vacío)"
            />
          </div>
          <div>
            <Label className="text-[#A3A3A3]">Français</Label>
            <Input
              value={formData.name_fr}
              onChange={(e) => handleChange('name_fr', e.target.value)}
              className="bg-[#0a0a0a] border-[#333] text-white"
              placeholder="(usa español si vacío)"
            />
          </div>
        </div>
      </div>

      {/* Descriptions */}
      <div className="space-y-4">
        <h3 className="text-white font-bold border-b border-[#333] pb-2">Descripción</h3>
        <div className="space-y-4">
          <div>
            <Label className="text-[#A3A3A3]">Español *</Label>
            <Textarea
              value={formData.description_es}
              onChange={(e) => handleChange('description_es', e.target.value)}
              className="bg-[#0a0a0a] border-[#333] text-white min-h-[80px]"
              required
            />
          </div>
          <div>
            <Label className="text-[#A3A3A3]">English</Label>
            <Textarea
              value={formData.description_en}
              onChange={(e) => handleChange('description_en', e.target.value)}
              className="bg-[#0a0a0a] border-[#333] text-white min-h-[80px]"
              placeholder="(usa español si vacío)"
            />
          </div>
          <div>
            <Label className="text-[#A3A3A3]">Français</Label>
            <Textarea
              value={formData.description_fr}
              onChange={(e) => handleChange('description_fr', e.target.value)}
              className="bg-[#0a0a0a] border-[#333] text-white min-h-[80px]"
              placeholder="(usa español si vacío)"
            />
          </div>
        </div>
      </div>

      {/* Price, Category, Badge */}
      <div className="space-y-4">
        <h3 className="text-white font-bold border-b border-[#333] pb-2">Detalles</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-[#A3A3A3]">Precio (€) *</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={formData.price}
              onChange={(e) => handleChange('price', e.target.value)}
              className="bg-[#0a0a0a] border-[#333] text-white"
              required
            />
          </div>
          <div>
            <Label className="text-[#A3A3A3]">Categoría *</Label>
            <Select value={formData.category} onValueChange={(v) => handleChange('category', v)}>
              <SelectTrigger className="bg-[#0a0a0a] border-[#333] text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#333]">
                <SelectItem value="taser">Taser</SelectItem>
                <SelectItem value="alarm">Alarma</SelectItem>
                <SelectItem value="spray">Spray</SelectItem>
                <SelectItem value="hammer">Martillo</SelectItem>
                <SelectItem value="kubaton">Kubaton</SelectItem>
                <SelectItem value="pack">Pack</SelectItem>
                <SelectItem value="accessory">Accesorio</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[#A3A3A3]">Badge (opcional)</Label>
            <Input
              value={formData.badge}
              onChange={(e) => handleChange('badge', e.target.value)}
              className="bg-[#0a0a0a] border-[#333] text-white"
              placeholder="ej: BEST SELLER, NEW"
            />
          </div>
        </div>
        <div className="flex gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.in_stock}
              onChange={(e) => handleChange('in_stock', e.target.checked)}
              className="w-4 h-4 accent-[#FF0099]"
            />
            <span className="text-white">En Stock</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.featured}
              onChange={(e) => handleChange('featured', e.target.checked)}
              className="w-4 h-4 accent-[#FF0099]"
            />
            <span className="text-white">Destacado</span>
          </label>
        </div>
      </div>

      {/* Main Image */}
      <div className="space-y-4">
        <h3 className="text-white font-bold border-b border-[#333] pb-2">Imagen Principal</h3>
        <ImageUploader
          value={formData.image_url}
          onChange={(url) => handleChange('image_url', url)}
          label="Imagen del producto *"
        />
      </div>

      {/* Colors */}
      <div className="space-y-4">
        <h3 className="text-white font-bold border-b border-[#333] pb-2">Colores Disponibles</h3>
        <div>
          <Label className="text-[#A3A3A3]">Colores (separados por coma)</Label>
          <Input
            value={formData.colors}
            onChange={(e) => handleChange('colors', e.target.value)}
            className="bg-[#0a0a0a] border-[#333] text-white"
            placeholder="Black, Pink, Silver, Gold"
          />
        </div>
      </div>

      {/* Color Images */}
      <div className="space-y-4">
        <h3 className="text-white font-bold border-b border-[#333] pb-2">Imágenes por Color</h3>
        
        {/* Existing color images */}
        {Object.entries(colorImages).length > 0 && (
          <div className="space-y-2">
            {Object.entries(colorImages).map(([color, url]) => (
              <div key={color} className="flex items-center gap-3 bg-[#0a0a0a] p-2 border border-[#333]">
                <img src={url} alt={color} className="w-10 h-10 object-cover" onError={(e) => e.target.style.display = 'none'} />
                <span className="text-white flex-1">{color}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveColorImage(color)}
                  className="text-red-500 hover:text-red-400"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Add new color image */}
        <div className="space-y-2">
          <div className="flex gap-2">
            <Input
              value={newColorName}
              onChange={(e) => setNewColorName(e.target.value)}
              className="bg-[#0a0a0a] border-[#333] text-white"
              placeholder="Nombre del color (ej: Black)"
            />
          </div>
          <ColorImageUploader
            value={newColorImage}
            onChange={setNewColorImage}
            onAdd={() => {
              if (newColorName && newColorImage) {
                setColorImages(prev => ({ ...prev, [newColorName]: newColorImage }));
                setNewColorName('');
                setNewColorImage('');
              }
            }}
            colorName={newColorName}
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-4 pt-4 border-t border-[#333]">
        <Button type="submit" className="btn-primary flex-1" disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          {product ? 'Guardar Cambios' : 'Crear Producto'}
        </Button>
        <Button type="button" onClick={onCancel} className="btn-secondary">
          Cancelar
        </Button>
      </div>
    </form>
  );
};

export const Admin = () => {
  const { t, i18n } = useTranslation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  const lang = i18n.language;

  // Check if already authenticated from session storage
  useEffect(() => {
    const adminAuth = sessionStorage.getItem('adminAuth');
    if (adminAuth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        setIsAuthenticated(true);
        sessionStorage.setItem('adminAuth', 'true');
      }
    } catch (error) {
      setAuthError('Contraseña incorrecta');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('adminAuth');
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [activeTab, isAuthenticated]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'dashboard') {
        const [statsRes, productsRes] = await Promise.all([
          axios.get(`${API}/admin/stats`),
          axios.get(`${API}/products`)
        ]);
        setStats(statsRes.data);
        setProducts(productsRes.data);
      } else if (activeTab === 'products') {
        const res = await axios.get(`${API}/products`);
        setProducts(res.data);
      } else if (activeTab === 'orders') {
        const res = await axios.get(`${API}/admin/orders`);
        setOrders(res.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProduct = async (productData) => {
    setSaving(true);
    try {
      await axios.post(`${API}/products`, productData);
      toast.success('Producto creado correctamente');
      setProductDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Error al crear el producto');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateProduct = async (productData) => {
    setSaving(true);
    try {
      await axios.put(`${API}/products/${editingProduct.id}`, productData);
      toast.success('Producto actualizado correctamente');
      setProductDialogOpen(false);
      setEditingProduct(null);
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar el producto');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('¿Estás segura de que quieres eliminar este producto?')) return;
    try {
      await axios.delete(`${API}/products/${productId}`);
      toast.success('Producto eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar el producto');
    }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status?status=${status}`);
      toast.success('Estado del pedido actualizado');
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar el estado');
    }
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setProductDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingProduct(null);
    setProductDialogOpen(true);
  };

  const tabs = [
    { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { key: 'products', label: 'Productos', icon: Package },
    { key: 'orders', label: 'Pedidos', icon: ShoppingBag },
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'paid': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'shipped': return <Truck className="w-4 h-4 text-blue-500" />;
      case 'delivered': return <CheckCircle className="w-4 h-4 text-[#FF0099]" />;
      case 'cancelled': return <XCircle className="w-4 h-4 text-red-500" />;
      default: return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-500';
      case 'paid': return 'text-green-500';
      case 'shipped': return 'text-blue-500';
      case 'delivered': return 'text-[#FF0099]';
      case 'cancelled': return 'text-red-500';
      default: return 'text-white';
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center" data-testid="admin-login">
        <div className="w-full max-w-md mx-4">
          <div className="bg-[#1A1A1A] border border-[#333333] p-8">
            <h1 className="font-heading text-2xl text-white font-bold mb-2 text-center">
              Panel de Administración
            </h1>
            <p className="text-[#A3A3A3] text-center mb-6">
              Acceso restringido
            </p>
            
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label className="text-[#A3A3A3]">Contraseña</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-[#0a0a0a] border-[#333] text-white"
                  placeholder="Introduce la contraseña"
                  required
                />
              </div>
              
              {authError && (
                <p className="text-red-500 text-sm">{authError}</p>
              )}
              
              <Button type="submit" className="btn-primary w-full">
                Acceder
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl text-white font-bold">
            Panel de Administración
          </h1>
          <Button onClick={handleLogout} className="btn-secondary text-sm">
            Cerrar Sesión
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-3 font-bold uppercase tracking-wider text-sm whitespace-nowrap transition-all ${
                activeTab === tab.key
                  ? 'bg-[#FF0099] text-white'
                  : 'bg-[#1A1A1A] text-[#A3A3A3] hover:text-white border border-[#333333]'
              }`}
              data-testid={`tab-${tab.key}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#FF0099]" />
          </div>
        ) : (
          <>
            {/* Dashboard */}
            {activeTab === 'dashboard' && stats && (
              <div data-testid="dashboard-content">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  <div className="bg-[#1A1A1A] border border-[#333333] p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-[#A3A3A3] text-sm uppercase tracking-wider">Total Productos</p>
                        <p className="font-heading text-3xl text-white font-bold mt-2">{stats.total_products}</p>
                      </div>
                      <Package className="w-10 h-10 text-[#FF0099]" />
                    </div>
                  </div>

                  <div className="bg-[#1A1A1A] border border-[#333333] p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-[#A3A3A3] text-sm uppercase tracking-wider">Total Pedidos</p>
                        <p className="font-heading text-3xl text-white font-bold mt-2">{stats.total_orders}</p>
                      </div>
                      <ShoppingBag className="w-10 h-10 text-[#FAFF00]" />
                    </div>
                  </div>

                  <div className="bg-[#1A1A1A] border border-[#333333] p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-[#A3A3A3] text-sm uppercase tracking-wider">Pedidos Pendientes</p>
                        <p className="font-heading text-3xl text-white font-bold mt-2">{stats.pending_orders}</p>
                      </div>
                      <Clock className="w-10 h-10 text-yellow-500" />
                    </div>
                  </div>

                  <div className="bg-[#1A1A1A] border border-[#333333] p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-[#A3A3A3] text-sm uppercase tracking-wider">Ingresos</p>
                        <p className="font-heading text-3xl text-[#FF0099] font-bold mt-2">€{stats.total_revenue.toFixed(2)}</p>
                      </div>
                      <DollarSign className="w-10 h-10 text-green-500" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Products */}
            {activeTab === 'products' && (
              <div data-testid="products-content">
                <div className="flex justify-between items-center mb-6">
                  <p className="text-[#A3A3A3]">{products.length} productos</p>
                  <Button onClick={openCreateDialog} className="btn-primary" data-testid="add-product-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Añadir Producto
                  </Button>
                </div>

                <div className="bg-[#1A1A1A] border border-[#333333] overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#333333]">
                        <th className="text-left p-4 text-[#A3A3A3] text-sm uppercase tracking-wider">Producto</th>
                        <th className="text-left p-4 text-[#A3A3A3] text-sm uppercase tracking-wider">Categoría</th>
                        <th className="text-left p-4 text-[#A3A3A3] text-sm uppercase tracking-wider">Precio</th>
                        <th className="text-left p-4 text-[#A3A3A3] text-sm uppercase tracking-wider">Stock</th>
                        <th className="text-right p-4 text-[#A3A3A3] text-sm uppercase tracking-wider">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.map((product) => (
                        <tr key={product.id} className="border-b border-[#333333]/50 hover:bg-[#333333]/20">
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <img 
                                src={product.image_url} 
                                alt={product.name[lang]} 
                                className="w-12 h-12 object-cover"
                              />
                              <div>
                                <p className="text-white font-bold text-sm">
                                  {product.name[lang] || product.name.en}
                                </p>
                                {product.badge && (
                                  <span className="text-[#FAFF00] text-xs">{product.badge}</span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="p-4">
                            <span className="text-[#A3A3A3] capitalize">{product.category}</span>
                          </td>
                          <td className="p-4">
                            <span className="text-[#FF0099] font-bold">€{product.price.toFixed(2)}</span>
                          </td>
                          <td className="p-4">
                            <span className={product.in_stock ? 'text-green-500' : 'text-red-500'}>
                              {product.in_stock ? 'En Stock' : 'Agotado'}
                            </span>
                          </td>
                          <td className="p-4 text-right">
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={() => openEditDialog(product)}
                                className="text-[#A3A3A3] hover:text-[#FF0099] transition-colors p-2"
                                data-testid={`edit-product-${product.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteProduct(product.id)}
                                className="text-[#A3A3A3] hover:text-red-500 transition-colors p-2"
                                data-testid={`delete-product-${product.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Orders */}
            {activeTab === 'orders' && (
              <div data-testid="orders-content">
                <p className="text-[#A3A3A3] mb-6">{orders.length} pedidos</p>

                {orders.length === 0 ? (
                  <div className="bg-[#1A1A1A] border border-[#333333] p-8 text-center">
                    <ShoppingBag className="w-12 h-12 text-[#333333] mx-auto mb-4" />
                    <p className="text-[#A3A3A3]">No hay pedidos todavía</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {orders.map((order) => (
                      <div 
                        key={order.id} 
                        className="bg-[#1A1A1A] border border-[#333333] p-4"
                        data-testid={`order-${order.id}`}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                          <div>
                            <p className="text-white font-bold">
                              Pedido #{order.id.slice(0, 8)}...
                            </p>
                            <p className="text-[#A3A3A3] text-sm">
                              {new Date(order.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                              {getStatusIcon(order.status)}
                              <span className={`uppercase text-sm font-bold ${getStatusColor(order.status)}`}>
                                {order.status}
                              </span>
                            </div>
                            <Select 
                              value={order.status} 
                              onValueChange={(value) => handleUpdateOrderStatus(order.id, value)}
                            >
                              <SelectTrigger className="w-[140px] bg-[#0a0a0a] border-[#333]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-[#1A1A1A] border-[#333]">
                                <SelectItem value="pending">Pendiente</SelectItem>
                                <SelectItem value="paid">Pagado</SelectItem>
                                <SelectItem value="shipped">Enviado</SelectItem>
                                <SelectItem value="delivered">Entregado</SelectItem>
                                <SelectItem value="cancelled">Cancelado</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="border-t border-[#333333] pt-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <p className="text-[#A3A3A3] text-sm mb-2">Cliente:</p>
                              <p className="text-white">{order.shipping?.name}</p>
                              <p className="text-[#A3A3A3] text-sm">{order.shipping?.email}</p>
                              <p className="text-[#A3A3A3] text-sm">{order.shipping?.address}</p>
                              <p className="text-[#A3A3A3] text-sm">{order.shipping?.city}, {order.shipping?.postal_code}</p>
                            </div>
                            <div>
                              <p className="text-[#A3A3A3] text-sm mb-2">Productos:</p>
                              {order.items?.map((item, idx) => (
                                <p key={idx} className="text-white text-sm">
                                  {item.quantity}x {item.name} {item.color && `(${item.color})`}
                                </p>
                              ))}
                              <p className="text-[#FF0099] font-bold mt-2">
                                Total: €{order.total?.toFixed(2)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Product Dialog */}
        <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
          <DialogContent className="bg-[#1A1A1A] border-[#333333] max-w-3xl">
            <DialogHeader>
              <DialogTitle className="text-white font-heading text-xl">
                {editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
              </DialogTitle>
            </DialogHeader>
            <ProductForm
              product={editingProduct}
              onSave={editingProduct ? handleUpdateProduct : handleCreateProduct}
              onCancel={() => {
                setProductDialogOpen(false);
                setEditingProduct(null);
              }}
              loading={saving}
            />
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};
